/**
 * Teams API module
 */
import type { Team } from '@overlayqa/shared';
import { fetchApi } from './client';
import { log } from '@/lib/logger';
import { MUTATION_TIMEOUT_MS, SCREEN_READ_TIMEOUT_MS } from './requestTimeout';

log.info('[api/teams] Module loaded');

export interface TeamMemberSummary {
  id: string;
  userId: string;
  role: 'owner' | 'admin' | 'member';
  createdAt: string;
  user: {
    id: string;
    email: string;
    name: string;
    avatarUrl: string | null;
  } | null;
}

export interface TeamInvitationSummary {
  id: string;
  email: string;
  role: 'admin' | 'member';
  expiresAt: string;
  createdAt: string;
}

export interface InvitationLookup {
  accepted?: boolean;
  id: string;
  email: string;
  role: 'admin' | 'member';
  expiresAt: string;
  team: { id: string; name: string; slug: string };
  invitedBy: { name: string; email: string } | null;
}

export interface AcceptInvitationResult {
  teamId: string;
  role: 'admin' | 'member';
  alreadyMember: boolean;
}

/**
 * Custom branding for a team's client-facing shares (spec 2026-09-01).
 * `entitled` rides alongside the record because the settings section renders
 * for a LAPSED team too (no active plan): read-only, with the link to pick a
 * plan. Branding is on every active plan since 2026-09-16, so false never
 * means "wrong tier".
 */
export interface TeamBranding {
  logoUrl: string | null;
  accentColor: string | null;
  backgroundColor: string | null;
  hidePoweredBy: boolean;
}

export interface TeamBrandingResponse {
  branding: TeamBranding;
  entitled: boolean;
  /**
   * One of the team's own client reviews, for "See it on a client review"
   * (audit 2026-09-03). Null when the team has none yet.
   */
  sampleReviewUrl: string | null;
  /**
   * Whether the team may hide "Powered by OverlayQA": only on a paid plan,
   * never during the trial (Emily, 2026-11-01). `branding.hidePoweredBy` stays
   * the stored choice; client pages honour it only while this is true.
   */
  canHidePoweredBy: boolean;
}

/** The server's contrast verdict on a candidate accent, as the preview returns it. */
export type BrandingAccentVerdict =
  | { ok: true; hex: string; ratio: number }
  | { ok: false; reason: 'invalid' }
  | { ok: false; reason: 'low_contrast'; hex: string; ratio: number; suggestion: string | null };

/**
 * The live preview (audit 2026-09-03, decision 12(a)): the REAL client-review
 * header rendered on the server for colours the user has not saved. `html` is a
 * complete document for a sandboxed iframe; the verdicts are the server's.
 */
export interface TeamBrandingPreview {
  html: string;
  accent: BrandingAccentVerdict | null;
  background: { ok: true; hex: string } | { ok: false; reason: 'invalid' } | null;
  /** Advisory only; an upload still succeeds when its artwork is hard to see. */
  logo?: { status: 'unavailable' } | { status: 'empty' }
    | { status: 'checked'; lowContrastSurfaces: Array<'review' | 'portal' | 'shared-issues'> }
    | null;
}

export interface TeamBrandingPreviewParams {
  /** Raw field text. Empty means "use the default", which differs from absent. */
  accent: string;
  background: string;
  hidePoweredBy: boolean;
  /**
   * The STORED logo, read by the server, never sent. It is here so the query
   * key changes when a logo is uploaded or removed; the logo mutations
   * invalidate the branding key and not this one, so without it the cached
   * frame outlived the upload (frame 13, 2026-09-03).
   */
  logoUrl: string | null;
}

export const teamsApi = {
  list: (token: string | undefined) =>
    fetchApi<Team[]>('/teams', { token, timeoutMs: SCREEN_READ_TIMEOUT_MS }),

  get: (token: string | undefined, id: string) =>
    fetchApi<Team>(`/teams/${id}`, { token, timeoutMs: SCREEN_READ_TIMEOUT_MS }),

  create: (token: string | undefined, data: { name: string; slug: string; ownerId: string }) =>
    fetchApi<Team>('/teams', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(data),
    }),

  // ── Members ────────────────────────────────────────────────
  listMembers: (token: string | undefined, teamId: string) =>
    fetchApi<TeamMemberSummary[]>(`/teams/${teamId}/members`, { token, timeoutMs: SCREEN_READ_TIMEOUT_MS }),

  removeMember: (token: string | undefined, teamId: string, userId: string) =>
    fetchApi<{ removed: true }>(`/teams/${teamId}/members/${userId}`, {
      token,
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),

  leaveTeam: (token: string | undefined, teamId: string) =>
    fetchApi<{ left: true }>(`/teams/${teamId}/leave`, {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),

  // ── Invitations (team-scoped) ──────────────────────────────
  listInvitations: (token: string | undefined, teamId: string) =>
    fetchApi<TeamInvitationSummary[]>(`/teams/${teamId}/invitations`, { token, timeoutMs: SCREEN_READ_TIMEOUT_MS }),

  invite: (
    token: string | undefined,
    teamId: string,
    data: { email: string; role: 'admin' | 'member' },
    // Every caller (onboarding, useInviteTeamMember) handles the server's
    // refusals itself (seat cap 403, already-a-member 409, email-provider 500)
    // and declares them here so a handled refusal logs at warn instead of
    // minting a fake $exception.
    options: { expectedErrorStatuses?: readonly number[] } = {},
  ) =>
    fetchApi<TeamInvitationSummary>(`/teams/${teamId}/invite`, {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(data),
      ...options,
    }),

  revokeInvitation: (token: string | undefined, teamId: string, invitationId: string) =>
    fetchApi<{ revoked: true }>(`/teams/${teamId}/invitations/${invitationId}`, {
      token,
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
    }),

  // ── Invitations (token-scoped, used by /invite/:token page) ─
  // Public lookup — no auth required, the token IS the auth. Every refusal
  // here is a state the /invite page renders (404 unknown; 410 revoked /
  // accepted / expired, team-invite.service.ts), so they are expected: an
  // un-declared 4xx logs at error and mints a fake $exception (measured
  // 2026-09-02, verify-team-concurrency.ts S8).
  lookupInvitation: (inviteToken: string, authToken?: string) =>
    fetchApi<InvitationLookup>(`/teams/invitations/${inviteToken}`, { token: authToken, expectedErrorStatuses: [404, 410], timeoutMs: SCREEN_READ_TIMEOUT_MS }),

  acceptInvitation: (authToken: string | undefined, inviteToken: string) =>
    fetchApi<AcceptInvitationResult>(`/teams/invitations/${inviteToken}/accept`, {
      token: authToken,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      expectedErrorStatuses: [404, 409, 410],
    }),

  // ── Branding (spec 2026-09-01) ──────────────────────────────
  // Every call passes `token`. Omitting it is not a small bug: the whole
  // feature 401s for every signed-in user while its component tests, which
  // mock this module away, stay green. That shipped once already, on
  // tokenSourcesApi.
  getBranding: (token: string | undefined, teamId: string) =>
    fetchApi<TeamBrandingResponse>(`/teams/${teamId}/branding`, { token, timeoutMs: SCREEN_READ_TIMEOUT_MS }),

  updateBranding: (
    token: string | undefined,
    teamId: string,
    data: { accentColor?: string | null; backgroundColor?: string | null; hidePoweredBy?: boolean },
  ) =>
    fetchApi<TeamBranding>(`/teams/${teamId}/branding`, {
      token,
      method: 'PATCH',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(data),
    }),

  uploadBrandingLogo: (token: string | undefined, teamId: string, dataUrl: string, filename: string) =>
    fetchApi<TeamBranding>(`/teams/${teamId}/branding/logo`, {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify({ dataUrl, filename }),
    }),

  deleteBrandingLogo: (token: string | undefined, teamId: string) =>
    fetchApi<TeamBranding>(`/teams/${teamId}/branding/logo`, { token, method: 'DELETE', timeoutMs: MUTATION_TIMEOUT_MS }),

  // A GET on purpose: it fires on a debounce while the user types, and the
  // READ rate bucket is the one sized for that. Both colours are ALWAYS sent,
  // empty when the field is empty, because the server reads an absent param as
  // "use the stored value" and an empty one as "use the default".
  previewBranding: (token: string | undefined, teamId: string, params: TeamBrandingPreviewParams) => {
    const query = new URLSearchParams({
      accent: params.accent,
      background: params.background,
      hidePoweredBy: params.hidePoweredBy ? '1' : '0',
    });
    return fetchApi<TeamBrandingPreview>(`/teams/${teamId}/branding/preview?${query.toString()}`, { token });
  },
};
